import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Play,
  Users,
  BookOpen,
  Clock,
  Loader2,
  X,
  Filter,
  MessageSquare,
  ClipboardCheck,
  Star,
  Zap,
  Merge,
  PlusCircle,
  XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/components/common/Badge';
import { Spinner } from '@/components/common/Spinner';
import { cn } from '@/utils/cn';
import { MarkdownEditor } from '@/components/common/MarkdownEditor';
import { useTrainingStore } from '@/store/trainingStore';
import { useSimulationStore } from '@/store/simulationStore';
import { useTeamStore } from '@/store/teamStore';
import { trainingService } from '@/services/training.service';
import { skillsService } from '@/services/skills.service';
import type {
  TrainingGround,
  TrainingScenario,
  TrainingAssignment,
  TrainingSession,
  TrainingReview,
  SkillExtractionJob,
  SkillExtractionCandidate,
  Skill,
} from '@/types';

type TabType = 'scenarios' | 'assignments' | 'sessions' | 'reviews';

interface ScenarioFormData {
  name: string;
  bot_id: string;
  scenario_guidance: string;
  difficulty: 'easy' | 'medium' | 'hard';
}

const defaultScenarioForm: ScenarioFormData = {
  name: '',
  bot_id: '',
  scenario_guidance: '',
  difficulty: 'medium',
};

export const TrainingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { startSession, sessionLoading } = useTrainingStore();
  const { bots, fetchBots } = useSimulationStore();
  const { members, fetchMembers } = useTeamStore();

  const [ground, setGround] = useState<TrainingGround | null>(null);
  const [isLoadingGround, setIsLoadingGround] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('scenarios');

  // Scenarios
  const [scenarios, setScenarios] = useState<TrainingScenario[]>([]);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  const [showScenarioModal, setShowScenarioModal] = useState(false);
  const [scenarioForm, setScenarioForm] = useState<ScenarioFormData>(defaultScenarioForm);
  const [isSavingScenario, setIsSavingScenario] = useState(false);
  const [startingScenarioId, setStartingScenarioId] = useState<string | null>(null);

  // Assignments
  const [assignments, setAssignments] = useState<TrainingAssignment[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [isSavingAssignment, setIsSavingAssignment] = useState(false);

  // Sessions
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionStatusFilter, setSessionStatusFilter] = useState<string>('');

  // Session messages viewer
  const [viewingSession, setViewingSession] = useState<TrainingSession | null>(null);
  const [sessionMessages, setSessionMessages] = useState<Array<{ id: string; sender_type: string; content: string; created_at: string }>>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  // Reviews
  const [reviews, setReviews] = useState<TrainingReview[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [selectedReviewIds, setSelectedReviewIds] = useState<string[]>([]);
  const [isBulkApproving, setIsBulkApproving] = useState(false);
  const [reviewConversation, setReviewConversation] = useState<TrainingReview | null>(null);
  const [reviewMessages, setReviewMessages] = useState<Array<{ id: string; sender_type: string; content: string; created_at: string }>>([]);
  const [reviewMessagesLoading, setReviewMessagesLoading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionJobs, setExtractionJobs] = useState<SkillExtractionJob[]>([]);

  // Extraction results modal
  const [showExtractionModal, setShowExtractionModal] = useState(false);
  const [extractionJobDetail, setExtractionJobDetail] = useState<SkillExtractionJob | null>(null);
  const [candidates, setCandidates] = useState<SkillExtractionCandidate[]>([]);
  const [existingSkills, setExistingSkills] = useState<Skill[]>([]);
  const [extractionResultsLoading, setExtractionResultsLoading] = useState(false);
  const [processingCandidateId, setProcessingCandidateId] = useState<string | null>(null);
  const [mergeTargetSkillId, setMergeTargetSkillId] = useState<string>('');
  const [showMergeSelect, setShowMergeSelect] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    loadGround();
    fetchBots().catch(() => {});
    fetchMembers().catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!id) return;
    if (activeTab === 'scenarios') loadScenarios();
    if (activeTab === 'assignments') loadAssignments();
    if (activeTab === 'sessions') loadSessions();
    if (activeTab === 'reviews') loadReviews();
  }, [activeTab, id]);

  const loadGround = async () => {
    if (!id) return;
    setIsLoadingGround(true);
    try {
      const data = await trainingService.getGround(id);
      setGround(data);
    } catch {
      toast.error('Failed to load training ground');
      navigate('/training');
    } finally {
      setIsLoadingGround(false);
    }
  };

  const loadScenarios = async () => {
    if (!id) return;
    setScenariosLoading(true);
    try {
      const data = await trainingService.getScenarios(id);
      setScenarios(data);
    } catch {
      toast.error('Failed to load scenarios');
    } finally {
      setScenariosLoading(false);
    }
  };

  const loadAssignments = async () => {
    if (!id) return;
    setAssignmentsLoading(true);
    try {
      const data = await trainingService.getAssignments(id);
      setAssignments(data);
    } catch {
      toast.error('Failed to load assignments');
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const loadSessions = async () => {
    if (!id) return;
    setSessionsLoading(true);
    try {
      const params: { ground_id: string; status?: string } = { ground_id: id };
      if (sessionStatusFilter) params.status = sessionStatusFilter;
      const data = await trainingService.getSessions(params);
      setSessions(data);
    } catch {
      toast.error('Failed to load sessions');
    } finally {
      setSessionsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'sessions' && id) loadSessions();
  }, [sessionStatusFilter]);

  useEffect(() => {
    if (activeTab === 'reviews' && id) {
      loadExtractionJobs();
    }
  }, [activeTab, id]);

  // Scenario handlers
  const handleCreateScenario = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    if (!scenarioForm.name.trim()) {
      toast.error('Scenario name is required');
      return;
    }
    if (!scenarioForm.bot_id) {
      toast.error('Please select a bot');
      return;
    }

    setIsSavingScenario(true);
    try {
      const created = await trainingService.createScenario(id, {
        name: scenarioForm.name.trim(),
        bot_id: scenarioForm.bot_id,
        scenario_guidance: scenarioForm.scenario_guidance.trim(),
        difficulty: scenarioForm.difficulty,
      });
      setScenarios((prev) => [...prev, created]);
      setShowScenarioModal(false);
      setScenarioForm(defaultScenarioForm);
      toast.success('Scenario created');
    } catch {
      toast.error('Failed to create scenario');
    } finally {
      setIsSavingScenario(false);
    }
  };

  const handleDeleteScenario = async (scenarioId: string, scenarioName: string) => {
    if (!id) return;
    if (!confirm(`Delete scenario "${scenarioName}"?`)) return;

    try {
      await trainingService.deleteScenario(id, scenarioId);
      setScenarios((prev) => prev.filter((s) => s.id !== scenarioId));
      toast.success('Scenario deleted');
    } catch {
      toast.error('Failed to delete scenario');
    }
  };

  const handleStartTraining = async (scenarioId: string) => {
    setStartingScenarioId(scenarioId);
    try {
      await startSession(scenarioId);
      const session = useTrainingStore.getState().activeSession;
      if (session) {
        navigate(`/training/session/${session.id}`);
      }
    } catch {
      toast.error('Failed to start training session');
    } finally {
      setStartingScenarioId(null);
    }
  };

  // Assignment handlers
  const handleAssignMembers = async () => {
    if (!id || selectedUserIds.length === 0) return;

    setIsSavingAssignment(true);
    try {
      const created = await trainingService.createAssignments(id, selectedUserIds);
      setAssignments((prev) => [...prev, ...created]);
      setShowAssignModal(false);
      setSelectedUserIds([]);
      toast.success('Members assigned');
    } catch {
      toast.error('Failed to assign members');
    } finally {
      setIsSavingAssignment(false);
    }
  };

  const handleRemoveAssignment = async (assignmentId: string) => {
    if (!id) return;
    if (!confirm('Remove this assignment?')) return;

    try {
      await trainingService.removeAssignment(id, assignmentId);
      setAssignments((prev) => prev.filter((a) => a.id !== assignmentId));
      toast.success('Assignment removed');
    } catch {
      toast.error('Failed to remove assignment');
    }
  };

  const handleViewSession = async (session: TrainingSession) => {
    setViewingSession(session);
    setMessagesLoading(true);
    try {
      const messages = await trainingService.getSessionMessages(session.id);
      setSessionMessages(messages);
    } catch {
      toast.error('Failed to load session messages');
    } finally {
      setMessagesLoading(false);
    }
  };

  const toggleUserId = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const getDifficultyBadge = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'success';
      case 'medium':
        return 'warning';
      case 'hard':
        return 'error';
      default:
        return 'default';
    }
  };

  const getSessionStatusBadge = (status: string) => {
    switch (status) {
      case 'in_progress':
        return 'info';
      case 'completed':
        return 'success';
      case 'abandoned':
        return 'warning';
      default:
        return 'default';
    }
  };

  const loadReviews = async () => {
    if (!id) return;
    setReviewsLoading(true);
    try {
      const data = await trainingService.getReviews(undefined, id);
      setReviews(data);
    } catch {
      toast.error('Failed to load reviews');
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedReviewIds.length === 0) return;
    setIsBulkApproving(true);
    try {
      await trainingService.bulkApproveReviews(selectedReviewIds);
      toast.success(`${selectedReviewIds.length} reviews approved`);
      setSelectedReviewIds([]);
      loadReviews();
    } catch {
      toast.error('Failed to bulk approve');
    } finally {
      setIsBulkApproving(false);
    }
  };

  const handleViewReview = async (review: TrainingReview) => {
    setReviewConversation(review);
    setReviewMessagesLoading(true);
    try {
      const messages = await trainingService.getSessionMessages(review.session_id);
      setReviewMessages(messages);
    } catch {
      toast.error('Failed to load messages');
    } finally {
      setReviewMessagesLoading(false);
    }
  };

  const toggleReviewId = (reviewId: string) => {
    setSelectedReviewIds(prev =>
      prev.includes(reviewId) ? prev.filter(id => id !== reviewId) : [...prev, reviewId]
    );
  };

  const getReviewStatusBadge = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const handleStartExtraction = async () => {
    if (!id) return;
    setIsExtracting(true);
    try {
      await trainingService.startExtraction(id);
      toast.success('Skill extraction started! Refresh to check status.');
      const jobs = await trainingService.getExtractionJobs(id);
      setExtractionJobs(jobs);
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'Failed to start extraction');
    } finally {
      setIsExtracting(false);
    }
  };

  const loadExtractionJobs = async () => {
    if (!id) return;
    try {
      const jobs = await trainingService.getExtractionJobs(id);
      setExtractionJobs(jobs);
    } catch {}
  };

  const openExtractionResults = async (jobId: string) => {
    setShowExtractionModal(true);
    setExtractionResultsLoading(true);
    try {
      const [result, allSkills] = await Promise.all([
        skillsService.getExtractionJob(jobId),
        skillsService.getSkills(),
      ]);
      setExtractionJobDetail(result.job);
      setCandidates(result.candidates);
      setExistingSkills(allSkills);
    } catch {
      toast.error('Failed to load extraction results');
    } finally {
      setExtractionResultsLoading(false);
    }
  };

  const handleMergeCandidate = async (candidateId: string, targetSkillId: string) => {
    setProcessingCandidateId(candidateId);
    try {
      await skillsService.mergeCandidate(candidateId, targetSkillId);
      toast.success('Candidate merged into skill');
      setCandidates(prev => prev.map(c => c.id === candidateId ? { ...c, status: 'merged' as const } : c));
      setShowMergeSelect(null);
      const allSkills = await skillsService.getSkills();
      setExistingSkills(allSkills);
    } catch {
      toast.error('Failed to merge candidate');
    } finally {
      setProcessingCandidateId(null);
    }
  };

  const handleCreateFromCandidate = async (candidateId: string) => {
    setProcessingCandidateId(candidateId);
    try {
      await skillsService.createFromCandidate(candidateId);
      toast.success('Skill created from candidate');
      setCandidates(prev => prev.map(c => c.id === candidateId ? { ...c, status: 'created' as const } : c));
      const allSkills = await skillsService.getSkills();
      setExistingSkills(allSkills);
    } catch {
      toast.error('Failed to create skill');
    } finally {
      setProcessingCandidateId(null);
    }
  };

  const handleDismissCandidate = async (candidateId: string) => {
    setProcessingCandidateId(candidateId);
    try {
      await skillsService.dismissCandidate(candidateId);
      setCandidates(prev => prev.map(c => c.id === candidateId ? { ...c, status: 'dismissed' as const } : c));
    } catch {
      toast.error('Failed to dismiss');
    } finally {
      setProcessingCandidateId(null);
    }
  };

  const getCandidateStatusBadge = (status: string): 'warning' | 'info' | 'success' | 'default' => {
    switch (status) {
      case 'pending': return 'warning';
      case 'merged': return 'info';
      case 'created': return 'success';
      case 'dismissed': return 'default';
      default: return 'default';
    }
  };

  const parseRulesArray = (rules: string[] | string): string[] => {
    if (Array.isArray(rules)) return rules;
    try { return JSON.parse(rules as string); } catch { return []; }
  };

  const getStatusBadgeVariant = (status: string) => {
    return status === 'active' ? 'success' : 'default';
  };

  if (isLoadingGround || !ground) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const tabs: { id: TabType; label: string; icon: React.FC<{ className?: string }> }[] = [
    { id: 'scenarios', label: 'Scenarios', icon: BookOpen },
    { id: 'assignments', label: 'Assignments', icon: Users },
    { id: 'sessions', label: 'Sessions', icon: Clock },
    { id: 'reviews', label: 'Reviews', icon: ClipboardCheck },
  ];

  const assignedUserIds = new Set(assignments.map((a) => a.user_id));
  const unassignedMembers = members.filter((m) => !assignedUserIds.has(m.user_id));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/training')} className="btn btn-ghost">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold">{ground.name}</h1>
            <Badge variant={getStatusBadgeVariant(ground.status)}>
              {ground.status}
            </Badge>
          </div>
          {ground.description && (
            <p className="text-muted-foreground mt-1">{ground.description}</p>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="flex gap-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex items-center gap-2 px-4 py-3 border-b-2 transition-colors',
                  activeTab === tab.id
                    ? 'border-primary text-primary font-medium'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {/* Scenarios Tab */}
        {activeTab === 'scenarios' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Scenarios ({scenarios.length})
              </h2>
              <button
                onClick={() => {
                  setScenarioForm(defaultScenarioForm);
                  setShowScenarioModal(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-violet-500 text-white font-semibold rounded-xl hover:bg-violet-600 transition-colors shadow-[3px_3px_0px_#1a1a2e] text-sm"
              >
                <Plus className="w-4 h-4" />
                Add Scenario
              </button>
            </div>

            {scenariosLoading ? (
              <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-violet-600 animate-spin" />
              </div>
            ) : scenarios.length === 0 ? (
              <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12 text-center">
                <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No scenarios yet. Add one to get started.</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#1a1a2e] bg-gray-50">
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Bot</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Difficulty</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {scenarios.map((scenario) => (
                      <tr key={scenario.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium text-gray-900">{scenario.name}</p>
                          {scenario.scenario_guidance && (
                            <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{scenario.scenario_guidance}</p>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{scenario.bot_name || '-'}</td>
                        <td className="px-6 py-4">
                          <Badge variant={getDifficultyBadge(scenario.difficulty)}>{scenario.difficulty}</Badge>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleStartTraining(scenario.id)}
                              disabled={startingScenarioId === scenario.id || sessionLoading}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors text-xs disabled:opacity-50"
                            >
                              {startingScenarioId === scenario.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                              Start
                            </button>
                            <button
                              onClick={() => handleDeleteScenario(scenario.id, scenario.name)}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Assignments Tab */}
        {activeTab === 'assignments' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Assigned Members ({assignments.length})
              </h2>
              <button
                onClick={() => {
                  setSelectedUserIds([]);
                  setShowAssignModal(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-violet-500 text-white font-semibold rounded-xl hover:bg-violet-600 transition-colors shadow-[3px_3px_0px_#1a1a2e] text-sm"
              >
                <Plus className="w-4 h-4" />
                Assign Members
              </button>
            </div>

            {assignmentsLoading ? (
              <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-violet-600 animate-spin" />
              </div>
            ) : assignments.length === 0 ? (
              <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12 text-center">
                <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No members assigned yet.</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#1a1a2e] bg-gray-50">
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Assigned</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {assignments.map((assignment) => (
                      <tr key={assignment.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{assignment.user_name || 'Unknown'}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{assignment.user_email || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{new Date(assignment.created_at).toLocaleDateString()}</td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleRemoveAssignment(assignment.id)}
                            className="text-red-500 hover:text-red-600 text-xs flex items-center gap-1 font-medium"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Sessions</h2>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={sessionStatusFilter}
                  onChange={(e) => setSessionStatusFilter(e.target.value)}
                  className="px-3 py-1.5 border-2 border-[#1a1a2e] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-violet-500 outline-none"
                >
                  <option value="">All Statuses</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="abandoned">Abandoned</option>
                </select>
              </div>
            </div>

            {sessionsLoading ? (
              <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-violet-600 animate-spin" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12 text-center">
                <Clock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No sessions found.</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#1a1a2e] bg-gray-50">
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Trainer
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Scenario
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Turns
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Started
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sessions.map((session) => (
                      <tr key={session.id} onClick={() => handleViewSession(session)} className="hover:bg-gray-50 transition-colors cursor-pointer">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {session.trainer_name || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {session.scenario_name || 'Unknown'}
                        </td>
                        <td className="px-6 py-4">
                          <Badge variant={getSessionStatusBadge(session.status)}>
                            {session.status.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {session.total_turns}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(session.started_at).toLocaleString([], {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Reviews ({reviews.length})</h2>
              <div className="flex items-center gap-2">
                {selectedReviewIds.length > 0 && (
                  <button
                    onClick={handleBulkApprove}
                    disabled={isBulkApproving}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors shadow-[3px_3px_0px_#1a1a2e] text-sm disabled:opacity-50"
                  >
                    {isBulkApproving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />}
                    Approve Selected ({selectedReviewIds.length})
                  </button>
                )}
                <button
                  onClick={handleStartExtraction}
                  disabled={isExtracting}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 transition-colors shadow-[3px_3px_0px_#1a1a2e] text-sm disabled:opacity-50"
                >
                  {isExtracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                  Extract Skills
                </button>
              </div>
            </div>

            {reviewsLoading ? (
              <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-violet-600 animate-spin" />
              </div>
            ) : reviews.length === 0 ? (
              <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12 text-center">
                <ClipboardCheck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No reviews yet. Complete some training sessions first.</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#1a1a2e] bg-gray-50">
                      <th className="w-12 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedReviewIds.length === reviews.filter(r => r.status === 'pending').length && reviews.filter(r => r.status === 'pending').length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedReviewIds(reviews.filter(r => r.status === 'pending').map(r => r.id));
                            } else {
                              setSelectedReviewIds([]);
                            }
                          }}
                          className="w-4 h-4 rounded border-[#1a1a2e]/15 text-violet-600 focus:ring-[#1a1a2e]/20"
                        />
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Reviewer</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Score</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reviews.map((review) => (
                      <tr key={review.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => handleViewReview(review)}>
                        <td className="w-12 px-4 py-4" onClick={(e) => e.stopPropagation()}>
                          {review.status === 'pending' && (
                            <input
                              type="checkbox"
                              checked={selectedReviewIds.includes(review.id)}
                              onChange={() => toggleReviewId(review.id)}
                              className="w-4 h-4 rounded border-[#1a1a2e]/15 text-violet-600 focus:ring-[#1a1a2e]/20"
                            />
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{review.reviewer_name || 'Unreviewed'}</td>
                        <td className="px-6 py-4">
                          <Badge variant={getReviewStatusBadge(review.status)}>{review.status}</Badge>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {review.score !== null ? (
                            <div className="flex items-center gap-1">
                              {[1,2,3,4,5].map(s => (
                                <Star key={s} className={cn('w-3.5 h-3.5', s <= (review.score ?? 0) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300')} />
                              ))}
                            </div>
                          ) : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">{new Date(review.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Extraction Jobs */}
            {extractionJobs.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Extraction Jobs</h3>
                <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#1a1a2e] bg-gray-50">
                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Candidates</th>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tokens</th>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Cost</th>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {extractionJobs.map((job) => (
                        <tr key={job.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <Badge variant={job.status === 'completed' ? 'success' : job.status === 'failed' ? 'error' : job.status === 'processing' ? 'info' : 'warning'}>
                              {job.status}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">{job.candidate_count}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{((job.total_input_tokens || 0) + (job.total_output_tokens || 0)).toLocaleString()}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">${(job.total_cost_usd || 0).toFixed(4)}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{new Date(job.created_at).toLocaleString()}</td>
                          <td className="px-6 py-4">
                            {job.status === 'completed' && job.candidate_count > 0 && (
                              <button
                                onClick={() => openExtractionResults(job.id)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors text-xs"
                              >
                                <Zap className="w-3 h-3" />
                                View Results
                              </button>
                            )}
                            {job.status === 'processing' && (
                              <span className="inline-flex items-center gap-1 text-xs text-violet-600">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Processing...
                              </span>
                            )}
                            {job.status === 'failed' && job.error_message && (
                              <span className="text-xs text-red-500 truncate max-w-[150px] block" title={job.error_message}>
                                {job.error_message}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Scenario Modal */}
      {showScenarioModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowScenarioModal(false)} />
          <div className="relative bg-white rounded-xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Add Scenario</h2>
              </div>
              <button
                onClick={() => setShowScenarioModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleCreateScenario} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={scenarioForm.name}
                  onChange={(e) => setScenarioForm({ ...scenarioForm, name: e.target.value })}
                  placeholder="e.g. Angry Customer Refund Request"
                  className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-violet-500 outline-none text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Bot <span className="text-red-500">*</span>
                </label>
                <select
                  value={scenarioForm.bot_id}
                  onChange={(e) => setScenarioForm({ ...scenarioForm, bot_id: e.target.value })}
                  className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-violet-500 outline-none text-sm bg-white"
                >
                  <option value="">Select a bot...</option>
                  {bots.map((bot) => (
                    <option key={bot.id} value={bot.id}>
                      {bot.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Difficulty
                </label>
                <select
                  value={scenarioForm.difficulty}
                  onChange={(e) =>
                    setScenarioForm({
                      ...scenarioForm,
                      difficulty: e.target.value as 'easy' | 'medium' | 'hard',
                    })
                  }
                  className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-violet-500 outline-none text-sm bg-white"
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>

              <div>
                <MarkdownEditor
                  label="Scenario Guidance"
                  value={scenarioForm.scenario_guidance}
                  onChange={(val) =>
                    setScenarioForm({ ...scenarioForm, scenario_guidance: val })
                  }
                  placeholder="Describe the scenario, customer persona, and expected interaction flow..."
                  rows={8}
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#1a1a2e]">
                <button
                  type="button"
                  onClick={() => setShowScenarioModal(false)}
                  className="px-4 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSavingScenario || !scenarioForm.name.trim() || !scenarioForm.bot_id}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-violet-500 text-white font-semibold rounded-xl hover:bg-violet-600 transition-colors shadow-[3px_3px_0px_#1a1a2e] disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {isSavingScenario ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Add Scenario
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Members Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAssignModal(false)} />
          <div className="relative bg-white rounded-xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-500 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Assign Members</h2>
              </div>
              <button
                onClick={() => setShowAssignModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {unassignedMembers.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">All team members are already assigned.</p>
              </div>
            ) : (
              <div className="space-y-2 mb-6">
                {unassignedMembers.map((member) => (
                  <label
                    key={member.user_id}
                    className={cn(
                      'flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all',
                      selectedUserIds.includes(member.user_id)
                        ? 'border-violet-500 bg-violet-50'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(member.user_id)}
                      onChange={() => toggleUserId(member.user_id)}
                      className="sr-only"
                    />
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
                      {(member.user_name || member.user_email || '?')[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {member.user_name || 'Unknown'}
                      </p>
                      {member.user_email && (
                        <p className="text-xs text-gray-500">{member.user_email}</p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#1a1a2e]">
              <button
                type="button"
                onClick={() => setShowAssignModal(false)}
                className="px-4 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignMembers}
                disabled={isSavingAssignment || selectedUserIds.length === 0}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-violet-500 text-white font-semibold rounded-xl hover:bg-violet-600 transition-colors shadow-[3px_3px_0px_#1a1a2e] disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {isSavingAssignment ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Assign ({selectedUserIds.length})
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session Messages Modal */}
      {viewingSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => { setViewingSession(null); setSessionMessages([]); }} />
          <div className="relative bg-white rounded-xl p-6 max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-500 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Session Conversation</h2>
                  <p className="text-sm text-gray-500">
                    {viewingSession.scenario_name || 'Unknown Scenario'} &middot; {viewingSession.trainer_name || 'Unknown Trainer'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => { setViewingSession(null); setSessionMessages([]); }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="flex items-center gap-3 mb-4">
              <Badge variant={getSessionStatusBadge(viewingSession.status)}>
                {viewingSession.status.replace('_', ' ')}
              </Badge>
              <span className="text-sm text-gray-500">{viewingSession.total_turns} turns</span>
              <span className="text-sm text-gray-500">
                {new Date(viewingSession.started_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>

            <div className="flex-1 overflow-y-auto space-y-3 min-h-0 border-2 border-[#1a1a2e] rounded-xl p-4 bg-gray-50">
              {messagesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-violet-600 animate-spin" />
                </div>
              ) : sessionMessages.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No messages in this session.</p>
                </div>
              ) : (
                sessionMessages.map((msg) => {
                  const isHuman = msg.sender_type === 'human' || msg.sender_type === 'agent';
                  return (
                    <div key={msg.id} className={cn('flex', isHuman ? 'justify-end' : 'justify-start')}>
                      <div className={cn(
                        'max-w-[75%] rounded-lg p-3',
                        isHuman ? 'bg-violet-500 text-white' : 'bg-white text-gray-900 border-2 border-[#1a1a2e]'
                      )}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={cn('text-xs font-semibold', isHuman ? 'text-violet-100' : 'text-gray-500')}>
                            {isHuman ? 'Trainer' : 'Customer'}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Extraction Results Modal (Two-Panel) */}
      {showExtractionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowExtractionModal(false)} />
          <div className="relative bg-white rounded-xl max-w-6xl w-full max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 pb-4 border-b border-[#1a1a2e] flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Extraction Results</h2>
                  {extractionJobDetail && (
                    <p className="text-sm text-gray-500">{extractionJobDetail.candidate_count} candidates found &middot; ${(extractionJobDetail.total_cost_usd || 0).toFixed(4)} cost</p>
                  )}
                </div>
              </div>
              <button onClick={() => setShowExtractionModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Body */}
            {extractionResultsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
              </div>
            ) : (
              <div className="flex-1 overflow-hidden grid grid-cols-5 gap-0">
                {/* Left: Candidates (60%) */}
                <div className="col-span-3 flex flex-col border-r border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900 px-4 py-3 bg-gray-50 border-b border-[#1a1a2e] flex-shrink-0">Extracted Candidates</h3>
                  <div className="flex-1 overflow-y-auto">
                    {candidates.map((c) => {
                      const candidateRules = parseRulesArray(c.rules);
                      const candidateExamples = Array.isArray(c.few_shot_examples) ? c.few_shot_examples : [];
                      return (
                        <div key={c.id} className="px-4 py-3 border-b border-[#1a1a2e] hover:bg-gray-50">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="text-sm font-semibold text-gray-900 truncate">{c.name}</p>
                                <Badge variant={c.skill_type === 'common' ? 'info' : 'default'}>{c.skill_type}</Badge>
                                <Badge variant={getCandidateStatusBadge(c.status)}>{c.status}</Badge>
                              </div>
                              <p className="text-xs text-gray-500 mb-1">{candidateRules.length} rules, {candidateExamples.length} examples</p>
                              {candidateRules.length > 0 && (
                                <div className="mt-1.5 space-y-1">
                                  {candidateRules.slice(0, 2).map((rule, i) => (
                                    <p key={i} className="text-xs text-gray-600 bg-gray-50 rounded px-2 py-1 truncate">{typeof rule === 'string' ? rule : (rule as any).pattern || (rule as any).rule || JSON.stringify(rule)}</p>
                                  ))}
                                  {candidateRules.length > 2 && (
                                    <p className="text-xs text-gray-400">+{candidateRules.length - 2} more rules</p>
                                  )}
                                </div>
                              )}
                              {c.suggested_match_name && c.suggested_match_skill_id && c.status === 'pending' && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleMergeCandidate(c.id, c.suggested_match_skill_id!); }}
                                  disabled={processingCandidateId === c.id}
                                  className="inline-flex items-center gap-1 mt-2 px-2.5 py-1 text-xs font-medium text-violet-700 bg-violet-50 border-2 border-[#1a1a2e] rounded-md hover:bg-violet-100 transition-colors disabled:opacity-50"
                                >
                                  <Merge className="w-3 h-3" />
                                  Merge into &ldquo;{c.suggested_match_name}&rdquo;
                                </button>
                              )}
                              {c.suggested_match_name && !c.suggested_match_skill_id && (
                                <p className="text-xs text-violet-600 mt-1">Suggested: {c.suggested_match_name}</p>
                              )}
                            </div>
                            {c.status === 'pending' && (
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {showMergeSelect === c.id ? (
                                  <div className="flex items-center gap-1">
                                    <select
                                      value={mergeTargetSkillId}
                                      onChange={(e) => setMergeTargetSkillId(e.target.value)}
                                      className="px-2 py-1 border-2 border-[#1a1a2e] rounded text-xs max-w-[140px]"
                                    >
                                      <option value="">Select skill...</option>
                                      {existingSkills.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                    </select>
                                    <button
                                      onClick={() => mergeTargetSkillId && handleMergeCandidate(c.id, mergeTargetSkillId)}
                                      disabled={!mergeTargetSkillId || processingCandidateId === c.id}
                                      className="px-2 py-1 bg-violet-500 text-white rounded text-xs disabled:opacity-50 hover:bg-violet-600"
                                    >
                                      {processingCandidateId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Go'}
                                    </button>
                                    <button onClick={() => setShowMergeSelect(null)} className="p-1 text-gray-400 hover:text-gray-600">
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => { setShowMergeSelect(c.id); setMergeTargetSkillId(c.suggested_match_skill_id || ''); }}
                                      className="p-1.5 text-violet-600 hover:bg-violet-50 rounded-lg"
                                      title="Merge into existing skill"
                                    >
                                      <Merge className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleCreateFromCandidate(c.id)}
                                      disabled={processingCandidateId === c.id}
                                      className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg"
                                      title="Create as new skill"
                                    >
                                      {processingCandidateId === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />}
                                    </button>
                                    <button
                                      onClick={() => handleDismissCandidate(c.id)}
                                      disabled={processingCandidateId === c.id}
                                      className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg"
                                      title="Dismiss candidate"
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Right: Existing Skills (40%) */}
                <div className="col-span-2 flex flex-col">
                  <h3 className="text-sm font-semibold text-gray-900 px-4 py-3 bg-gray-50 border-b border-[#1a1a2e] flex-shrink-0">Existing Skills</h3>
                  <div className="flex-1 overflow-y-auto">
                    {existingSkills.map((s) => (
                      <div key={s.id} className="px-4 py-3 border-b border-[#1a1a2e] hover:bg-gray-50 flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{s.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge variant={s.skill_type === 'common' ? 'info' : 'default'}>{s.skill_type}</Badge>
                            <span className="text-xs text-gray-400">v{s.version}</span>
                            <span className="text-xs text-gray-400">{s.rules?.length || 0} rules</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {existingSkills.length === 0 && (
                      <div className="px-4 py-8 text-center text-sm text-gray-500">No existing skills yet</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Review Conversation Modal */}
      {reviewConversation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => { setReviewConversation(null); setReviewMessages([]); }} />
          <div className="relative bg-white rounded-xl p-6 max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center">
                  <ClipboardCheck className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Review Conversation</h2>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge variant={getReviewStatusBadge(reviewConversation.status)}>{reviewConversation.status}</Badge>
                    {reviewConversation.score !== null && (
                      <span className="text-sm text-gray-500">{reviewConversation.score}/5</span>
                    )}
                  </div>
                </div>
              </div>
              <button onClick={() => { setReviewConversation(null); setReviewMessages([]); }} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {reviewConversation.feedback && (
              <div className="bg-gray-50 rounded-xl p-3 mb-4">
                <p className="text-xs font-semibold text-gray-500 mb-1">Feedback</p>
                <p className="text-sm text-gray-700">{reviewConversation.feedback}</p>
              </div>
            )}

            <div className="flex-1 overflow-y-auto space-y-3 min-h-0 border-2 border-[#1a1a2e] rounded-xl p-4 bg-gray-50">
              {reviewMessagesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-violet-600 animate-spin" />
                </div>
              ) : reviewMessages.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No messages found.</p>
                </div>
              ) : (
                reviewMessages.map((msg) => {
                  const isHuman = msg.sender_type === 'human' || msg.sender_type === 'agent';
                  return (
                    <div key={msg.id} className={cn('flex', isHuman ? 'justify-end' : 'justify-start')}>
                      <div className={cn(
                        'max-w-[75%] rounded-lg p-3',
                        isHuman ? 'bg-violet-500 text-white' : 'bg-white text-gray-900 border-2 border-[#1a1a2e]'
                      )}>
                        <span className={cn('text-xs font-semibold block mb-0.5', isHuman ? 'text-violet-100' : 'text-gray-500')}>
                          {isHuman ? 'Trainer' : 'Customer'}
                        </span>
                        <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingDetailPage;
